import React from "react";
import PropTypes from "prop-types";
import { Card, Divider, IconButton } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";

const formatCurrency = (value) => {
  if (typeof value === "string" && !isNaN(value)) {
    value = Number(value);
  }
  if (typeof value === "number" && !isNaN(value)) {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }
  if (typeof value === "string" && value.includes("$")) {
    return value;
  }
  return "$ 0";
};

const getMoraStatusColor = (rangoMora) => {
  if (rangoMora == "AL DIA") {
    return "#3cc20a";
  } else if (rangoMora == "MENOS DE 90 DIAS") {
    return "#f1950f";
  } else if (rangoMora == "MAS DE 90 DIAS") {
    return "#ff0000";
  } else {
    return "#9b21d5";
  }
};

function DeudaCard({ deuda, onEdit, onDelete }) {
  // Verificar si es libranza (case insensitive)
  const isLibranza = deuda.naturaleza && deuda.naturaleza.toLowerCase().includes("libranza");
  const hasPagadorLibranza =
    isLibranza && deuda.pagador_libranza && deuda.pagador_libranza.nombre_completo;

  return (
    <Card
      sx={{
        height: "100%",
        minHeight: "300px",
        maxHeight: "450px", // Aumentado ligeramente para acomodar la nueva información
        width: "350px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <SuiBox p={1.5} display="flex" flexDirection="column" height="100%">
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
          <Grid size={9}>
            <SuiTypography
              variant="h6"
              //fontWeight="medium"
              textTransform="uppercase"
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minHeight: "20px",
              }}
            >
              {deuda.str_acreedor?.nombre || ""} {deuda.str_acreedor?.apellido || ""}
            </SuiTypography>
          </Grid>
          <Grid size={3}>
            <SuiBox display="flex" justifyContent="flex-start" mb={0.25}>
              <IconButton
                onClick={() => onEdit(deuda)}
                size="small"
                sx={{
                  mr: 1,
                  color: "info.main",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <EditIcon fontSize="small" color="secondary" />
              </IconButton>
              <IconButton
                onClick={() => {
                  if (window.confirm("¿Está seguro(a) que desea eliminar esta deuda?")) {
                    onDelete(deuda);
                  }
                }}
                size="small"
                sx={{
                  color: "error.main",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </SuiBox>
          </Grid>
        </Grid>
        <SuiBox>
          <SuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <SuiBox display="flex" alignItems="center">
              {deuda?.codeudor_avalista && (
                <SuiBox
                  display="flex"
                  alignItems="center"
                  sx={{
                    backgroundColor: "info.main",
                    borderRadius: "4px",
                    padding: "4px 8px",
                    mr: deuda.postergado ? 1 : 0,
                  }}
                >
                  <SuiTypography
                    variant="caption"
                    fontWeight="bold"
                    color="white"
                    sx={{ lineHeight: "1" }}
                  >
                    CODEUDOR O AVALISTA
                  </SuiTypography>
                </SuiBox>
              )}
              {deuda.postergado && (
                <SuiBox
                  display="flex"
                  alignItems="center"
                  sx={{
                    backgroundColor: "warning.main",
                    borderRadius: "4px",
                    padding: "4px 8px",
                  }}
                >
                  <PriorityHighIcon sx={{ fontSize: 16, color: "white", mr: 0.25 }} />
                  <SuiTypography
                    variant="caption"
                    fontWeight="bold"
                    color="white"
                    sx={{ lineHeight: "1" }}
                  >
                    POSTERGADO
                  </SuiTypography>
                </SuiBox>
              )}
            </SuiBox>
          </SuiBox>
          <SuiBox>
            <SuiTypography variant="button" fontWeight="medium">
              {deuda.str_acreedor?.tipo_identificacion || "CC/NIT"}:
            </SuiTypography>
            <SuiTypography variant="button" fontWeight="regular" ml={1}>
              {deuda.str_acreedor?.identificacion || ""}
            </SuiTypography>
          </SuiBox>
          {Array.isArray(deuda.str_acreedor?.correos) && deuda.str_acreedor.correos.length > 0 && (
            <SuiBox>
              <SuiBox>
                {deuda.str_acreedor.correos
                  .sort((a, b) => a.orden - b.orden)
                  .slice(0, 2)
                  .map((correo, index) => (
                    <SuiBox
                      key={correo.id || index}
                      display="flex"
                      alignItems="center"
                      sx={{
                        backgroundColor: "warning.main",
                        borderRadius: "4px",
                        padding: "4px 8px",
                      }}
                    >
                      {/* <SuiBox key={correo.id || index} ml={1} mt={0.1}> */}
                      <AlternateEmailIcon sx={{ fontSize: 5, color: "white", mr: 0.25 }} />
                      <SuiTypography
                        variant="button"
                        fontWeight="regular"
                        sx={{
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {correo.email}
                      </SuiTypography>
                    </SuiBox>
                  ))}
                {deuda.str_acreedor.correos.length > 2 && (
                  <SuiTypography
                    variant="button"
                    fontWeight="regular"
                    ml={1}
                    mt={0.1}
                    color="text.secondary"
                  >
                    (+{deuda.str_acreedor.correos.length - 2} más)
                  </SuiTypography>
                )}
              </SuiBox>
            </SuiBox>
          )}
          {deuda.str_acreedor?.numero_contacto && (
            <SuiBox>
              <SuiTypography variant="button" fontWeight="medium">
                TELÉFONO:
              </SuiTypography>
              <SuiTypography variant="button" fontWeight="regular" ml={1}>
                {deuda.str_acreedor.numero_contacto}
              </SuiTypography>
            </SuiBox>
          )}
          {deuda.str_acreedor?.direccion && (
            <SuiBox>
              <SuiTypography variant="button" fontWeight="medium">
                DIRECCIÓN:
              </SuiTypography>
              <SuiTypography
                variant="button"
                fontWeight="regular"
                ml={1}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {deuda.str_acreedor.direccion}
              </SuiTypography>
            </SuiBox>
          )}
        </SuiBox>
        <Divider sx={{ my: 0.25 }} />
        <SuiBox>
          <SuiTypography variant="button" fontWeight="medium">
            CAPITAL:
          </SuiTypography>
          <SuiTypography variant="button" fontWeight="regular" ml={1}>
            {formatCurrency(deuda.capital)}
          </SuiTypography>
        </SuiBox>
        {deuda.reconoce_intereses && (
          <SuiBox>
            <SuiTypography variant="button" fontWeight="medium">
              INTERESES:
            </SuiTypography>
            <SuiTypography variant="button" fontWeight="regular" ml={1}>
              {formatCurrency(deuda.intereses)}
            </SuiTypography>
          </SuiBox>
        )}
        <SuiBox>
          <SuiTypography variant="button" fontWeight="medium">
            NATURALEZA:
          </SuiTypography>
          <SuiTypography variant="button" fontWeight="regular" ml={1}>
            {deuda.naturaleza?.toUpperCase()}
          </SuiTypography>
        </SuiBox>
        {hasPagadorLibranza && (
          <>
            <SuiBox
              sx={{
                backgroundColor: "rgba(33, 150, 243, 0.08)", // Fondo azul muy sutil
                borderRadius: "6px",
                padding: "8px",
                border: "2px solid rgba(33, 150, 243, 0.12)",
              }}
            >
              <SuiBox display="flex" alignItems="center">
                <SuiTypography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  sx={{ textTransform: "uppercase" }}
                >
                  Pagador Libranza
                </SuiTypography>
              </SuiBox>
              <SuiBox mb={0.1}>
                <SuiTypography
                  variant="caption"
                  fontWeight="regular"
                  ml={0.5}
                  sx={{
                    fontSize: "0.7rem",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {deuda.pagador_libranza.nombre_completo}
                </SuiTypography>
              </SuiBox>
              {deuda.pagador_libranza.email && (
                <SuiBox mb={0.1}>
                  <SuiTypography
                    variant="caption"
                    fontWeight="regular"
                    ml={0.5}
                    sx={{
                      fontSize: "0.7rem",
                      display: "-webkit-box",
                      WebkitLineClamp: 1,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {deuda.pagador_libranza.email} -{" "}
                    {deuda.pagador_libranza.celular || "No tiene celular"}
                  </SuiTypography>
                </SuiBox>
              )}
            </SuiBox>
          </>
        )}
        {isLibranza && !hasPagadorLibranza && (
          <>
            <SuiBox
              sx={{
                backgroundColor: "rgba(33, 150, 243, 0.08)", // Fondo azul muy sutil
                borderRadius: "6px",
                padding: "8px",
                border: "2px solid rgba(33, 150, 243, 0.12)",
              }}
            >
              <SuiBox display="flex" alignItems="center">
                <SuiTypography
                  variant="caption"
                  fontWeight="bold"
                  color="info.main"
                  sx={{ textTransform: "uppercase" }}
                >
                  No tiene Pagador Libranza
                </SuiTypography>
              </SuiBox>
            </SuiBox>
          </>
        )}
        {deuda.tipo_garantia && (
          <SuiBox>
            <SuiTypography variant="button" fontWeight="medium">
              TIPO DE GARANTÍA:
            </SuiTypography>
            <SuiTypography variant="button" fontWeight="regular" ml={1}>
              {deuda.tipo_garantia?.toUpperCase()}
              {deuda.tipo_garantia === "Otro" && " ( " + deuda.garantia + " ) "}
            </SuiTypography>
          </SuiBox>
        )}
        <Divider sx={{ my: 0.25 }} />
        <SuiBox>
          <SuiTypography
            variant="button"
            fontWeight="medium"
            style={{
              color: getMoraStatusColor(deuda.rango_mora),
            }}
          >
            {deuda.rango_mora}
          </SuiTypography>
        </SuiBox>
      </SuiBox>
    </Card>
  );
}

DeudaCard.propTypes = {
  deuda: PropTypes.shape({
    str_acreedor: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
      apellido: PropTypes.string,
      tipo_identificacion: PropTypes.string,
      identificacion: PropTypes.string,
      direccion: PropTypes.string,
      numero_contacto: PropTypes.string,
      correos: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          email: PropTypes.string,
          orden: PropTypes.number,
        })
      ),
    }).isRequired,
    str_clase_deuda: PropTypes.shape({
      id: PropTypes.number,
      nombre: PropTypes.string,
    }).isRequired,
    capital: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    reconoce_intereses: PropTypes.bool,
    suma_intereses: PropTypes.bool,
    intereses: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    naturaleza: PropTypes.string,
    tipo_garantia: PropTypes.string,
    garantia: PropTypes.string,
    rango_mora: PropTypes.string,
    postergado: PropTypes.bool,
    codeudor_avalista: PropTypes.bool,
    // Nuevo PropType para pagador_libranza
    pagador_libranza: PropTypes.shape({
      nombre_completo: PropTypes.string,
      email: PropTypes.string,
      tipo_identificacion: PropTypes.string,
      identificacion: PropTypes.string,
      direccion: PropTypes.string,
      celular: PropTypes.string,
    }),
  }).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default DeudaCard;
